<script setup lang="ts">
import { flattenNavigation, navGroupsForCapabilities } from '~/utils/adminNavigation'
import type { NavDestination } from '~/utils/adminNavigation'

/**
 * Palette de commandes : atteindre n'importe quel écran du CMS au clavier.
 *
 * Elle ne cherche **que des destinations**, jamais du contenu. Taper « fiche »
 * mène à la liste des fiches, pas à telle fiche : chercher un contenu par son
 * nom se fait depuis la liste de sa rubrique, qui a déjà son filtre. Ce choix
 * garde la palette rapide et prévisible — une liste courte, sans appel réseau
 * ni résultat qui change selon l'état de la base.
 *
 * Elle est montée hors du layout, dans `app.vue` et dans `error.vue` : Nuxt
 * monte le second **à la place** du premier, et une palette posée dans le seul
 * `app.vue` manquerait à l'écran d'erreur — celui d'où l'on a le plus besoin
 * d'aller ailleurs.
 */

const { paletteOpen, close } = useCommandPalette()
const auth = useAuthStore()
const router = useRouter()

/**
 * Le même filtre par rôle que la barre latérale, appliqué par la même
 * fonction : proposer une destination que l'écran refusera en 403 est pire que
 * ne pas la proposer, l'utilisateur croit y avoir droit.
 */
const { personnalisation } = useMenuAdministration()

const destinations = computed(() => flattenNavigation(navGroupsForCapabilities({
  isAdmin: auth.isAdmin,
  isSuperAdmin: auth.isSuperAdmin,
  isGlobalSuperAdmin: auth.isGlobalSuperAdmin,
}, personnalisation.value)))

/**
 * Un seul groupe plutôt qu'un par rubrique.
 *
 * Les en-têtes de groupe de `UCommandPalette` sont plats : ils ne portent qu'un
 * niveau, là où l'arbre en compte autant qu'il veut. Le chemin passe donc par
 * `suffix`, que le composant cherche nativement — `keys: [label, description,
 * suffix]` — et surligne comme le libellé. « Site › Législation › Fiches »
 * tient alors sur la ligne, quelle que soit la profondeur.
 */
const groups = computed(() => [{
  id: 'destinations',
  items: destinations.value.map(destination => ({
    id: destination.id,
    label: destination.label,
    suffix: destination.chemin,
    icon: destination.icon,
    to: destination.to,
    /*
     * Champ de recherche seul, jamais affiché : Fuse ne normalise pas les
     * accents, et sans lui `media` ne trouve pas « Médiathèque » ni `theme`
     * « Thèmes ». Dans un CMS français, personne ne tape ses accents pour
     * aller vite.
     */
    recherche: destination.recherche,
    onSelect: () => allerA(destination),
  })),
}])

/**
 * La recherche porte sur le libellé, le chemin, et leur version sans accents.
 *
 * `resultLimit` est relevé de 12 — la valeur d'usine — à 50 : taper « site »
 * doit remonter *tout* le groupe Site, une vingtaine de destinations, et la
 * limite d'origine tronquerait en silence.
 */
const fuse = {
  fuseOptions: {
    ignoreLocation: true,
    threshold: 0.1,
    keys: ['label', 'suffix', 'recherche'],
  },
  resultLimit: 50,
}

function allerA(destination: NavDestination) {
  close()
  // Lien libre (#343) : une adresse externe ou à ouvrir à part sort du routeur.
  if (destination.nouvelOnglet) window.open(destination.to, '_blank', 'noopener')
  else if (destination.externe) window.location.href = destination.to
  else router.push(destination.to)
}

/*
 * `meta_k` bascule seul en Ctrl+K hors macOS (`defineShortcuts.js:208-210`) :
 * une seule déclaration couvre les trois systèmes.
 *
 * `usingInput` laisse le raccourci passer même lorsqu'un champ a le focus — on
 * ouvre souvent la palette depuis un formulaire à demi rempli, et l'y interdire
 * obligerait à cliquer ailleurs d'abord.
 */
defineShortcuts({
  meta_k: {
    usingInput: true,
    handler: () => { paletteOpen.value = !paletteOpen.value },
  },
})

/**
 * La recherche repart à vide à chaque ouverture.
 *
 * Retrouver la frappe précédente obligerait à l'effacer avant de chercher
 * autre chose — l'inverse du geste rapide que la palette promet.
 */
const searchTerm = ref('')

watch(paletteOpen, (ouverte) => {
  if (ouverte) searchTerm.value = ''
})
</script>

<template>
  <!--
    Le voile et le placement viennent d'`UModal` : la palette n'a pas à les
    refaire. `sm:max-w-2xl` lui donne de quoi afficher un chemin complet sans
    couper ; en dessous du point de rupture, la modale prend la largeur de
    l'écran, ce qui est le bon comportement sur téléphone.
  -->
  <UModal
    v-model:open="paletteOpen"
    :ui="{ content: 'sm:max-w-2xl' }"
    title="Aller à un écran"
    description="Rechercher une destination du CMS"
  >
    <template #content>
      <!--
        `lg` plutôt que la taille d'usine : elle donne aux lignes `p-2` au lieu
        de `p-1.5`, et au champ `h-13`. En `md`, une ligne de 28 pixels portant
        une icône de 20 en paraissait comprimée.

        L'icône de tête est ramenée de `size-5` à `size-4` : à 20 pixels face à
        un libellé de 14, elle pesait plus que le texte qu'elle annonce. À 16,
        le rapport rejoint celui de la barre latérale — `text-base` sur
        `text-sm` — et les deux se lisent pareil. Le réglage est nécessaire car
        aucune taille du composant ne dissocie l'icône du texte : `lg` garde
        `size-5`, et `xl` grossirait le libellé avec.

        `items-center` corrige un décalage mesuré de 1,5 pixel : la ligne est
        alignée `flex-start` par défaut, si bien que l'icône (16 pixels) et le
        libellé (boîte de 17) se calent par le haut et non sur leur centre.
        Ce défaut d'usine sert les entrées à description, qui passent sur deux
        lignes et veulent leur icône en haut ; les nôtres tiennent toutes sur
        une seule.
      -->
      <UCommandPalette
        v-model:search-term="searchTerm"
        :groups="groups"
        :fuse="fuse"
        size="lg"
        :ui="{ item: 'items-center', itemLeadingIcon: 'size-4' }"
        placeholder="Aller à un écran…"
        :close="true"
        class="h-80"
        @update:open="close"
      >
        <!-- Le conteneur du slot est déjà stylé par le composant : un simple texte suffit. -->
        <template #empty>
          Aucun écran ne correspond.
        </template>
      </UCommandPalette>
    </template>
  </UModal>
</template>
